import { ref } from 'vue'
import { useGeolocation } from '@/composables/useGeolocation'
import { useAppTracking } from '@/composables/useAppTracking'
import API from '@/services'
import type { UpdateUnitOnlinePayload, StatusBatchPayload, StatusBatchRecord } from '@/types'
import { useGlobalStore } from '@/stores/global'


export function useGPSTracking() {
    const globals = useGlobalStore()
    const posicion = ref('')
    // Wake Lock API
    let wakeLock: any = null
    // Para tracking por app
    const { fetchLocationEntries } = useAppTracking();
    let lastUtc: string | null = null;

    // Solicitar Wake Lock
    async function requestWakeLock() {
        try {
            // @ts-ignore
            if ('wakeLock' in navigator) {
                wakeLock = await (navigator as any).wakeLock.request('screen')
                wakeLock.addEventListener('release', () => {
                    console.log('WakeLock liberado')
                })
                console.log('WakeLock adquirido')
                posicion.value = 'WakeLock activo: pantalla encendida'
            } else {
                console.log('WakeLock API no soportada')
            }
        } catch (err) {
            console.warn('No se pudo adquirir WakeLock:', err)
            posicion.value = 'No se pudo activar WakeLock'
        }
    }

    // Liberar Wake Lock
    function releaseWakeLock() {
        try {
            if (wakeLock) {
                wakeLock.release()
                wakeLock = null
                console.log('WakeLock liberado')
                posicion.value = 'WakeLock liberado'
            }
        } catch (err) {
            console.warn('Error liberando WakeLock', err)
        }
    }

    // Manejar visibilidad de la pestaña
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            requestWakeLock()
        } else {
            releaseWakeLock()
        }
    })

    // Configuración optimizada para Safari iOS
    const { position, isUsingGPS, getCurrentPosition } = useGeolocation({
        enableHighAccuracy: true,
        timeout: 15000,        // 15 segundos - Safari iOS necesita más tiempo
        maximumAge: 0,    // 0 minutos - no permite usar caché s
        immediate: false       // No ejecutar inmediatamente para evitar problemas
    })

    // Función para verificar GPS o tracking por app
    const checkGPS = async () => {
        try {
            await requestWakeLock();
            if (globals.patrulla.useApp) {
                // Tracking por app: obtener posiciones desde el servicio externo
                let entries;
                if (!lastUtc) {
                    // Primera llamada: traer posiciones desde 3 minutos atrás
                    const now = new Date();
                    now.setMinutes(now.getMinutes() - 15);
                    const iso = now.toISOString();
                    entries = await fetchLocationEntries(iso);
                } else {
                    // Llamadas siguientes: traer solo las nuevas
                    // Sumar 1 segundo a la última fecha UTC
                    const lastDate = new Date(lastUtc);
                    lastDate.setSeconds(lastDate.getSeconds() + 1);
                    const iso = lastDate.toISOString();
                    entries = await fetchLocationEntries(iso);
                }
                if (entries && entries.length > 0) {
                    // Actualizar lastUtc
                    lastUtc = entries[entries.length - 1].utc;
                    // Mapear a records para batch
                    const records: StatusBatchRecord[] = entries.map(e => {
                        // Normalizar utc a formato 'YYYY-MM-DD HH:mm:ss.SSS' (3 dígitos de milisegundos)
                        let utc = e.utc;
                        // Separar fecha y hora
                        utc = utc.replace('T', ' ').replace('Z', '');
                        // Buscar el punto decimal
                        const match = utc.match(/(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})\.(\d+)/);
                        if (match) {
                            const base = match[1];
                            let ms = match[2].substring(0, 3); // Tomar solo los primeros 3 dígitos
                            while (ms.length < 3) ms += '0'; // Rellenar si es necesario
                            utc = `${base}.${ms}`;
                        }
                        return {
                            utc,
                            unit_id: globals.patrulla.unitId || "",
                            loc_lon: e.location.coordinates[0],
                            loc_lat: e.location.coordinates[1],
                            alt: 0,
                            cog: 0,
                            sog: 1,
                            cam_loc_lon: e.location.coordinates[0],
                            cam_loc_lat: e.location.coordinates[1],
                            pol_lon1: 0,
                            pol_lat1: 0,
                            pol_lon2: 0,
                            pol_lat2: 0,
                            pol_lon3: 0,
                            pol_lat3: 0,
                            pol_lon4: 0,
                            pol_lat4: 0,
                            state: "ACTIVE",
                            mission: globals.patrulla.activeMission || 0,
                            rssi: -85,
                            radio_temp: 25,
                            freq: 868
                        };
                    });
                    // Llamar a updateUnitOnline con la última posición
                    const last = records[records.length - 1];
                    const updatePayload: UpdateUnitOnlinePayload = {
                        parameters: {
                            unit_id: last.unit_id,
                            utc: last.utc,
                            version: "1.0",
                            mission: last.mission,
                            loc_lon: last.loc_lon,
                            loc_lat: last.loc_lat,
                            alt: last.alt,
                            cog: last.cog,
                            sog: last.sog,
                            cam_loc_lon: last.cam_loc_lon,
                            cam_loc_lat: last.cam_loc_lat,
                            cam_pol_lon1: 0,
                            cam_pol_lat1: 0,
                            cam_pol_lon2: 0,
                            cam_pol_lat2: 0,
                            cam_pol_lon3: 0,
                            cam_pol_lat3: 0,
                            cam_pol_lon4: 0,
                            cam_pol_lat4: 0
                        }
                    };
                    try {
                        await API.updateUnitOnline(updatePayload);
                        // Llamar a statusBatchRequest con todos los records
                        const batchPayload: StatusBatchPayload = {
                            parameters: { records }
                        };
                        await API.statusBatchRequest(batchPayload);
                        posicion.value = `Tracking app: ${records.length} posiciones enviadas`;
                    } catch (error) {
                        posicion.value = 'Error enviando datos de tracking app';
                        console.error('❌ Error tracking app:', error);
                    }
                } else {
                    posicion.value = 'Tracking app: sin nuevas posiciones';
                }
            } else {
                // Tracking por GPS nativo
                // ...existing code...
                // (todo el bloque original de GPS, sin cambios)
                // Verificar si la geolocalización está disponible
                if (!navigator.geolocation) {
                    posicion.value = 'Geolocalización no soportada';
                    return;
                }
                // Verificar permisos antes de solicitar ubicación
                if ('permissions' in navigator) {
                    try {
                        const permission = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
                        if (permission.state === 'denied') {
                            posicion.value = 'Permiso de ubicación denegado';
                            return;
                        }
                    } catch (permissionError) {
                        console.log('API de permisos no disponible');
                    }
                }
                await getCurrentPosition();
                const usingGPS = isUsingGPS();
                if (usingGPS === true) {
                    posicion.value = 'GPS activo - Alta precisión ' + JSON.stringify(position.value?.coords);
                    try {
                        const coords = position.value?.coords;
                        if (coords) {
                            const now = new Date();
                            const utcFormatted = now.getUTCFullYear() + '-' +
                                String(now.getUTCMonth() + 1).padStart(2, '0') + '-' +
                                String(now.getUTCDate()).padStart(2, '0') + ' ' +
                                String(now.getUTCHours()).padStart(2, '0') + ':' +
                                String(now.getUTCMinutes()).padStart(2, '0') + ':' +
                                String(now.getUTCSeconds()).padStart(2, '0') + '.' +
                                String(now.getUTCMilliseconds()).padStart(3, '0');
                            const updatePayload: UpdateUnitOnlinePayload = {
                                parameters: {
                                    unit_id: globals.patrulla.unitId || "",
                                    utc: utcFormatted,
                                    version: "1.0",
                                    mission: globals.patrulla.activeMission || 0,
                                    loc_lon: coords.longitude,
                                    loc_lat: coords.latitude,
                                    alt: coords.altitude || 0,
                                    cog: 0,
                                    sog: 1,
                                    cam_loc_lon: coords.longitude,
                                    cam_loc_lat: coords.latitude,
                                    cam_pol_lon1: 0,
                                    cam_pol_lat1: 0,
                                    cam_pol_lon2: 0,
                                    cam_pol_lat2: 0,
                                    cam_pol_lon3: 0,
                                    cam_pol_lat3: 0,
                                    cam_pol_lon4: 0,
                                    cam_pol_lat4: 0
                                }
                            };
                            await API.updateUnitOnline(updatePayload);
                            const batchPayload: StatusBatchPayload = {
                                parameters: {
                                    records: [
                                        {
                                            utc: utcFormatted,
                                            unit_id: globals.patrulla.unitId || "",
                                            loc_lon: coords.longitude,
                                            loc_lat: coords.latitude,
                                            alt: coords.altitude || 0,
                                            cog: 0,
                                            sog: 1,
                                            cam_loc_lon: coords.longitude,
                                            cam_loc_lat: coords.latitude,
                                            pol_lon1: 0,
                                            pol_lat1: 0,
                                            pol_lon2: 0,
                                            pol_lat2: 0,
                                            pol_lon3: 0,
                                            pol_lat3: 0,
                                            pol_lon4: 0,
                                            pol_lat4: 0,
                                            state: "ACTIVE",
                                            mission: globals.patrulla.activeMission || 0,
                                            rssi: -85,
                                            radio_temp: 25,
                                            freq: 868
                                        }
                                    ]
                                }
                            };
                            await API.statusBatchRequest(batchPayload);
                        }
                    } catch (error) {
                        console.error('❌ Error enviando datos GPS al servidor:', error);
                    }
                } else if (usingGPS === false) {
                    posicion.value = 'WiFi/Cellular - Precisión limitada ' + JSON.stringify(position.value?.coords);
                } else {
                    posicion.value = 'Sin datos de posición';
                }
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
            posicion.value = `Error tracking: ${errorMessage}`;
            console.error('Error tracking:', error);
        }
    };

    return {
        posicion,
        position,
        isUsingGPS,
        getCurrentPosition,
        checkGPS
    }
}
